import { Save, Video } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'

import { safeJsonParse } from '../utils/json-parser'

type ResolutionPriceMap = Record<string, Record<string, number>>

type ResolutionTierPricingProps = {
  value: string
  onChange: (value: string) => void
  onSave: () => void | Promise<void>
  isSaving: boolean
}

function formatDraftPrice(value: number): string {
  return String(Number(value.toFixed(12)))
}

function ResolutionPriceInput(props: {
  model: string
  resolution: string
  value: number
  onCommit: (value: number) => void
}) {
  const [draft, setDraft] = useState(formatDraftPrice(props.value))

  useEffect(() => {
    setDraft(formatDraftPrice(props.value))
  }, [props.value])

  const commit = useCallback(
    (raw: string) => {
      const price = Number(raw)
      if (!raw.trim() || !Number.isFinite(price) || price < 0) return false
      props.onCommit(price)
      return true
    },
    [props]
  )

  return (
    <label className='min-w-0 space-y-1'>
      <span className='text-muted-foreground block text-xs'>
        {props.resolution}
      </span>
      <div className='relative'>
        <Input
          aria-label={`${props.model} ${props.resolution}`}
          className='tabular-nums'
          type='number'
          min='0'
          step='0.001'
          value={draft}
          onChange={(event) => {
            const value = event.target.value
            setDraft(value)
            commit(value)
          }}
          onBlur={() => {
            if (!commit(draft)) setDraft(formatDraftPrice(props.value))
          }}
        />
      </div>
    </label>
  )
}

export function ResolutionTierPricing(props: ResolutionTierPricingProps) {
  const { t } = useTranslation()
  const prices = useMemo(
    () =>
      safeJsonParse<ResolutionPriceMap>(props.value, {
        fallback: {},
        silent: true,
      }),
    [props.value]
  )
  const models = useMemo(
    () =>
      Object.entries(prices)
        .map(([model, tiers]) => ({
          model,
          tiers: Object.entries(tiers).sort(([left], [right]) =>
            left.localeCompare(right, undefined, { numeric: true })
          ),
        }))
        .sort((left, right) => left.model.localeCompare(right.model)),
    [prices]
  )

  const updatePrice = useCallback(
    (model: string, resolution: string, price: number) => {
      props.onChange(
        JSON.stringify(
          { ...prices, [model]: { ...prices[model], [resolution]: price } },
          null,
          2
        )
      )
    },
    [prices, props]
  )

  if (models.length === 0) return null

  return (
    <Card>
      <CardHeader className='border-b'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div className='space-y-1'>
            <CardTitle className='flex items-center gap-2'>
              <Video className='size-4' />
              {t('Resolution tier pricing')}
            </CardTitle>
            <CardDescription>
              {t(
                'Set explicit per-second prices for each server-validated resolution. A missing tier falls back to the model base price.'
              )}
            </CardDescription>
          </div>
          <Button
            type='button'
            size='sm'
            onClick={props.onSave}
            disabled={props.isSaving}
          >
            <Save data-icon='inline-start' />
            {props.isSaving ? t('Saving...') : t('Save resolution prices')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className='pt-4'>
        <div className='grid gap-3 xl:grid-cols-3'>
          {models.map((item) => (
            <section key={item.model} className='rounded-lg border p-3'>
              <div className='mb-3 font-medium'>{item.model}</div>
              <div className='grid grid-cols-3 gap-2'>
                {item.tiers.map(([resolution, price]) => (
                  <ResolutionPriceInput
                    key={resolution}
                    model={item.model}
                    resolution={resolution}
                    value={price}
                    onCommit={(nextPrice) =>
                      updatePrice(item.model, resolution, nextPrice)
                    }
                  />
                ))}
              </div>
              <span className='text-muted-foreground mt-2 block text-[11px]'>
                {t('per second')}
              </span>
            </section>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
